import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { catchError, tap } from 'rxjs/operators';
import { SystemFile } from '../../../domain/common.model';
import { ContactData, EmailData } from '../../../domain/document.model';
import { Offer, OfferHeader, OfferRequest, PolicyholderRequest } from '../../../domain/offer.domain';
import { Party } from '../../../domain/party.domain';
import { Policy } from '../../../domain/policy.model';
import { Cession, InsuredProductMotor, InsuredProductProperty } from '../../../domain/product.domain';
import { OfferFilter } from '../../../domain/registry.domain';
import { SystemUser } from '../../../domain/user.model';
import { GenericService } from './generic.service';
import { UtilsService } from './utils.service';

@Injectable()
export class OfferService extends GenericService {
    constructor(private store: Store,
                private http: HttpClient) {
        super(store);
    }

    createCalculation(request: OfferRequest) {
        return this.http.post<SystemUser>(`/api/secured/offer`, request)
            .pipe(catchError(this.errorsMain), tap(this.cleanMain));
    }

    getAll(request: OfferFilter) {
        const params = UtilsService.buildQueryParams(request);
        return this.http.get<OfferHeader[]>(`/api/secured/offer`, {params});
    }

    getAllCSV(filter: OfferFilter) {
        const request = cloneDeep(filter);
        delete request.offset;
        delete request.limit;
        const params = UtilsService.buildQueryParams(request);
        return this.http.get<SystemFile>(`/api/secured/offer/export`, {params});
    }

    getOne(id: number) {
        return this.http.get<Offer>(`/api/secured/offer/${id}`);
    }

    deleteProduct(offerId: number, productId: number) {
        return this.http.delete<Offer>(`/api/secured/offer/${offerId}/product/${productId}`, {});
    }

    createProductProperty(id: number) {
        return this.http.post<InsuredProductProperty>(`/api/secured/offer/${id}/product/property`, {});
    }

    validateProperty(offerId: number, product: InsuredProductProperty) {
        return this.http.put<InsuredProductProperty>(`/api/secured/offer/${offerId}/product/property/${product.id}/basic`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    calculateProperty(offerId: number, product: InsuredProductProperty) {
        return this.http.put<InsuredProductProperty>(`/api/secured/offer/${offerId}/product/property/${product.id}/calculate`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    saveProperty(offerId: number, product: InsuredProductProperty) {
        return this.http.put<Offer>(`/api/secured/offer/${offerId}/product/property/${product.id}/details`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    createProductMotor(id: number) {
        return this.http.post<InsuredProductMotor>(`/api/secured/offer/${id}/product/motor`, {});
    }

    validateMotorBasic(offerId: number, product: InsuredProductMotor) {
        return this.http.put<InsuredProductMotor>(`/api/secured/offer/${offerId}/product/motor/${product.id}/basic`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    validateMotorClaims(offerId: number, product: InsuredProductMotor) {
        return this.http.put<InsuredProductMotor>(`/api/secured/offer/${offerId}/product/motor/${product.id}/claims`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    calculateMotor(offerId: number, product: InsuredProductMotor) {
        return this.http.put<InsuredProductMotor>(`/api/secured/offer/${offerId}/product/motor/${product.id}/calculate`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    saveMotor(offerId: number, product: InsuredProductMotor) {
        return this.http.put<Offer>(`/api/secured/offer/${offerId}/product/motor/${product.id}/details`, product.configuration)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    savePolicyholder(offerId: number, policyholder: PolicyholderRequest) {
        return this.http.put<Offer>(`/api/secured/offer/${offerId}/party/policyholder`, policyholder)
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    saveInsured(offerId: number, party: Party) {
        const http = party.id === null
            ? this.http.post<Offer>(`/api/secured/offer/${offerId}/party/insured`, {party})
            : this.http.put<Offer>(`/api/secured/offer/${offerId}/party/insured/${party.id}`, {party});
        return http
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    deleteInsured(offerId: number, party: Party) {
        return this.http.delete<Offer>(`/api/secured/offer/${offerId}/party/insured/${party.id}`, {})
            .pipe(catchError(this.errorsDialog), tap(this.cleanDialog));
    }

    saveLocation(calculationId: number, location: string) {
        return this.http.put<Offer>(`/api/secured/offer/${calculationId}/location`, {location})
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    saveCession(calculationId: number, insuredProductId: number, cession: Cession) {
        return this.http.put<Offer>(`/api/secured/offer/${calculationId}/product/${insuredProductId}/cession`, {cession})
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    deleteCession(calculationId: number, insuredProductId: number) {
        return this.http.delete<Offer>(`/api/secured/offer/${calculationId}/product/${insuredProductId}/cession`, {})
            .pipe(catchError(this.errorsDialog), tap(this.cleanDialog));
    }

    createOffer(calculation: Offer) {
        const clauseMap = calculation.additionalData.clauseMap;
        return this.http.put<Offer>(`/api/secured/offer/${calculation.id}/transition/offer`, {clauseMap})
            .pipe(catchError(this.errorsMain), tap(this.cleanMain));
    }

    backToCalculation(offerId: number) {
        return this.http.post<Offer>(`/api/secured/offer/${offerId}/clone`, {})
            .pipe(catchError(this.errorsMain), tap(this.cleanMain));
    }

    saveContactData(calculationId: number, contactData: ContactData) {
        return this.http.put<Offer>(`/api/secured/offer/${calculationId}/contact-data`, {contactData})
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    saveEmailData(offerId: number, emailData: EmailData) {
        return this.http.put<Offer>(`/api/secured/offer/${offerId}/email-data`, {emailData})
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }

    printoutCalculation(id: number, productId: number, all: boolean) {
        return all
            ? this.http.get<SystemFile>(`/api/secured/offer/${id}/product/${productId}/print/calculation/all`)
            : this.http.get<SystemFile>(`/api/secured/offer/${id}/product/${productId}/print/calculation`);
    }

    printoutOffer(id: number, productId: number, all: boolean) {
        return all
            ? this.http.get<SystemFile>(`/api/secured/offer/${id}/product/${productId}/print/offer/all`)
            : this.http.get<SystemFile>(`/api/secured/offer/${id}/product/${productId}/print/offer`);
    }

    issue(id: number) {
        return this.http.put<Policy[]>(`/api/secured/offer/${id}/issue`, {})
            .pipe(catchError(this.errorsMain), tap(this.cleanMain));
    }

    createIOffer(id: number) {
        return this.http.post<Offer>(`/api/secured/offer/${id}/ioffer`, {})
            .pipe(catchError(this.errorsAside), tap(this.cleanAside));
    }
}
